import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ShieldCheck, Zap } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api, fmtPrice, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function CheckoutDialog({ product, trigger }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [applied, setApplied] = useState(null);
  const [promoError, setPromoError] = useState("");
  const [applying, setApplying] = useState(false);

  const effectiveEmail = user ? user.email : email;
  const finalPrice = applied ? applied.amount : product.price;

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setApplying(true);
    setPromoError("");
    try {
      const { data } = await api.post("/checkout/validate-promo", {
        slug: product.slug,
        promo_code: promoInput.trim(),
      });
      if (data.valid) {
        setApplied({ code: promoInput.trim().toUpperCase(), amount: data.amount, percent: data.percent });
      } else {
        setApplied(null);
        setPromoError(
          data.reason === "limit_reached"
            ? "That code has reached its redemption limit."
            : data.reason === "not_applicable"
              ? "That code doesn't apply to this pack."
              : "That code isn't valid or has expired."
        );
      }
    } catch (err) {
      setApplied(null);
      setPromoError(formatApiError(err));
    } finally {
      setApplying(false);
    }
  };

  const checkout = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/create", {
        slug: product.slug,
        email: effectiveEmail,
        origin_url: window.location.origin,
        ...(applied ? { promo_code: applied.code } : {}),
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (typeof detail === "string" && detail.startsWith("promo_")) {
        setApplied(null);
        setPromoError("That code isn't valid or has expired. You can still check out at the normal price.");
      } else {
        toast.error(formatApiError(err));
      }
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        data-testid="checkout-dialog"
        className="border-white/15 bg-[#121418] text-slate-100 sm:max-w-md"
      >
        <DialogHeader>
          <div className="mono-label mb-2">Secure checkout</div>
          <DialogTitle className="font-display text-2xl font-bold uppercase leading-tight tracking-tight text-slate-100">
            {product.short_name}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-400">
            Digital download — delivered instantly after payment.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-1 flex items-center justify-between border border-white/10 bg-[#0A0B0D] px-4 py-3">
          <span className="text-sm text-slate-400">One-time payment</span>
          <span className="flex items-baseline gap-2">
            {applied && (
              <span data-testid="checkout-original-price" className="text-sm text-slate-500 line-through">
                {fmtPrice(product.price)}
              </span>
            )}
            <span data-testid="checkout-price" className="font-display text-2xl font-extrabold text-[#FF5500]">
              {fmtPrice(finalPrice)}
            </span>
          </span>
        </div>

        <form onSubmit={checkout} className="mt-3 space-y-4">
          {user ? (
            <p data-testid="checkout-account-note" className="text-sm text-slate-400">
              Checking out as <span className="font-semibold text-slate-200">{user.email}</span>
            </p>
          ) : (
            <div>
              <label className="mono-label mb-2 block" htmlFor="checkout-email">
                Email for your order &amp; Hub access
              </label>
              <Input
                id="checkout-email"
                data-testid="checkout-email-input"
                type="email"
                required
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="border-white/15 bg-[#0A0B0D] text-slate-100 placeholder:text-slate-600"
              />
            </div>
          )}

          <div>
            <label className="mono-label mb-2 block" htmlFor="checkout-promo">
              Promo code <span className="text-slate-500 normal-case">(if you have one)</span>
            </label>
            <div className="flex gap-2">
              <Input
                id="checkout-promo"
                data-testid="checkout-promo-input"
                value={promoInput}
                onChange={(e) => {
                  setPromoInput(e.target.value);
                  setPromoError("");
                }}
                disabled={!!applied}
                placeholder="Enter code"
                className="border-white/15 bg-[#0A0B0D] uppercase text-slate-100 placeholder:normal-case placeholder:text-slate-600"
              />
              {applied ? (
                <button
                  type="button"
                  data-testid="checkout-promo-remove"
                  onClick={() => {
                    setApplied(null);
                    setPromoInput("");
                    setPromoError("");
                  }}
                  className="shrink-0 border border-white/15 px-4 text-xs font-bold uppercase tracking-widest text-slate-300 transition-colors hover:border-[#FF5500] hover:text-[#FF5500]"
                >
                  Remove
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="checkout-promo-apply"
                  onClick={applyPromo}
                  disabled={applying || !promoInput.trim()}
                  className="shrink-0 border border-[#FF5500] px-4 text-xs font-bold uppercase tracking-widest text-[#FF5500] transition-colors hover:bg-[#FF5500] hover:text-white disabled:opacity-50"
                >
                  {applying ? "Checking…" : "Apply"}
                </button>
              )}
            </div>
            {applied && (
              <p data-testid="checkout-promo-applied" className="mt-2 text-xs text-[#FF5500]">
                Code applied — {applied.percent}% off. New total {fmtPrice(applied.amount)}.
              </p>
            )}
            {promoError && (
              <p data-testid="checkout-promo-error" className="mt-2 text-xs text-red-400">
                {promoError}
              </p>
            )}
          </div>

          <button
            data-testid="checkout-pay-button"
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 bg-[#FF5500] px-5 py-3.5 text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#E04800] disabled:opacity-60"
          >
            <Lock className="h-4 w-4" />
            {loading ? "Redirecting…" : `Pay ${fmtPrice(finalPrice)}`}
          </button>

          <div className="space-y-2 text-xs text-slate-500">
            <p className="flex items-center gap-2">
              <ShieldCheck className="h-3.5 w-3.5 text-[#FF5500]" />
              Secure card payment via Stripe. Apple Pay and Google Pay where your device supports them.
            </p>
            <p className="flex items-center gap-2">
              <Zap className="h-3.5 w-3.5 text-[#FF5500]" />
              Instant access in your Job Seeker Hub the moment payment clears.
            </p>
            {!user && (
              <p className="pl-5">
                No account needed.{" "}
                <Link to="/account" className="text-[#FF5500] underline underline-offset-2">
                  Create one
                </Link>{" "}
                to keep your purchase history.
              </p>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
